import React, { useState, useEffect } from 'react';
import { exploreDataset } from '../api';
import { EmptyState, InfoTooltip, ErrorState, SummaryTable, CorrelationMatrix} from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';

function DatasetExploration({ datasetName }) {
  const [selectedDataset, setSelectedDataset] = useState(datasetName || '');
  const [explorationData, setExplorationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update selectedDataset when datasetName prop changes
  useEffect(() => {
    if (datasetName) {
      setSelectedDataset(datasetName);
    }
  }, [datasetName]);

  const handleExploreDataset = async () => {
    if (!selectedDataset) {
      setError('Please select a dataset to explore.');
      return;
    }
    setLoading(true);
    setError(null);
    setExplorationData(null);
    try {
      const result = await exploreDataset(selectedDataset);
      setExplorationData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dataset-exploration">
      <InfoTooltip 
        title="Dataset Exploration" 
        content="Conduct comprehensive exploratory data analysis including statistical summaries, distributions, correlations, and data quality checks."
        position="top-left"
      >
        <h2 className="dataset-exploration__title">Dataset Exploration</h2>
      </InfoTooltip>
      
      <div className="dataset-exploration__controls">
        <label className="dataset-exploration__label">Dataset: </label>
        <input 
          type="text" 
          value={selectedDataset} 
          onChange={e => setSelectedDataset(e.target.value)} 
          placeholder="Enter dataset name"
          className="dataset-exploration__input"
        />
        <button 
          onClick={handleExploreDataset} 
          disabled={loading || !selectedDataset}
          className="dataset-exploration__explore-btn"
        >
          {loading ? 'Exploring...' : 'Explore Dataset'}
        </button>
      </div>
      
      {loading && (
        <div className="dataset-exploration__loading">
          <LoadingSpinner text="Conducting exploratory data analysis..." />
        </div>
      )}
      
      {error && (
        <ErrorState 
          message={error} 
          onRetry={() => {
            setError(null);
            handleExploreDataset();
          }}
        />
      )}
      
      {!selectedDataset && !loading && !error && !explorationData && (
        <EmptyState 
          message="Enter a dataset name above to start exploring your data."
          icon="🔍"
        />
      )}
      
      {explorationData && (
        <div className="dataset-exploration__results">
          <h3 className="dataset-exploration__results-title">Exploration Results</h3>
          {/* Cards (Rows/Cols/Missing) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
            <div className="p-4 bg-blue-50 border border-blue-100 rounded">
              <h4 className="font-bold text-blue-800">Rows</h4>
              <p className="text-2xl">{explorationData.shape[0]}</p>
            </div>
            <div className="p-4 bg-green-50 border border-green-100 rounded">
              <h4 className="font-bold text-green-800">Columns</h4>
              <p className="text-2xl">{explorationData.shape[1]}</p>
            </div>
            <div className="p-4 bg-purple-50 border border-purple-100 rounded">
              <h4 className="font-bold text-purple-800">Missing Values</h4>
              <p className="text-2xl">{explorationData.missing_data?.total_missing || 0}</p>
            </div>
          </div>

          {/* Descriptive Statistics (Mean, Std, Min, Max) */}
          {explorationData.numeric_analysis?.descriptive_stats && (
            <div className="descriptive-stats mb-8">
              <h3 className="text-lg font-bold mb-4">Descriptive Statistics</h3>
              <div style={{ display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', 
                            gap: '2rem' }}>
                <SummaryTable 
                  title="Mean (Average)" 
                  data={explorationData.numeric_analysis.descriptive_stats.mean} 
                />
                <SummaryTable 
                  title="Standard Deviation" 
                  data={explorationData.numeric_analysis.descriptive_stats.std} 
                />
                 <SummaryTable 
                  title="Minimum Values" 
                  data={explorationData.numeric_analysis.descriptive_stats.min} 
                />
                <SummaryTable 
                  title="Maximum Values" 
                  data={explorationData.numeric_analysis.descriptive_stats.max} 
                />
              </div>
            </div>
          )}

          {/* Correlation Matrix */}
          {explorationData.numeric_analysis?.correlation_matrix && (
            <div className="correlation-section">
              <h3 className="text-lg font-bold mb-4">Correlation Matrix</h3>
              <CorrelationMatrix 
                data={explorationData.numeric_analysis.correlation_matrix} 
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DatasetExploration;
